import {
  FormField,
  FieldGroups,
  FieldGroupConfig,
  fieldCategoryConfigs,
  personalFieldKeys,
  workFieldKeys,
  authFieldKeys,
} from './GenericFormTypes';

/**
 * Form Field Grouping Utilities
 * Provides logic for grouping form fields by category
 */

// =============================================================================
// Field Grouping Logic
// =============================================================================

/**
 * Group fields into categories (personal, work, auth)
 * @param fields - Array of form fields to group
 * @returns Grouped fields object
 */
export const getFieldGroups = (fields: FormField[]): FieldGroups => {
  const personalFields = fields.filter(f => personalFieldKeys.includes(f.key));

  const workFields = fields.filter(f => workFieldKeys.includes(f.key));

  const authFields = fields.filter(f => authFieldKeys.includes(f.key));

  return { personalFields, workFields, authFields };
};

/**
 * Get field group configurations with actual fields
 * @param fields - Array of form fields
 * @returns Array of field group configurations
 */
export const getFieldGroupConfigs = (fields: FormField[]): FieldGroupConfig[] => {
  const groups = getFieldGroups(fields);
  const configs: FieldGroupConfig[] = [];

  // Personal fields group
  if (groups.personalFields.length > 0) {
    configs.push({
      ...fieldCategoryConfigs.personal,
      fields: groups.personalFields,
    });
  }

  // Work fields group
  if (groups.workFields.length > 0) {
    configs.push({
      ...fieldCategoryConfigs.work,
      fields: groups.workFields,
    });
  }

  // Auth fields group
  if (groups.authFields.length > 0) {
    configs.push({
      ...fieldCategoryConfigs.auth,
      fields: groups.authFields,
    });
  }

  return configs;
};

/**
 * Check if a field belongs to a specific category
 * @param fieldKey - The field key to check
 * @param category - The category to check against
 * @returns Boolean indicating if field belongs to category
 */
export const isFieldInCategory = (
  fieldKey: string,
  category: 'personal' | 'work' | 'auth'
): boolean => {
  switch (category) {
    case 'personal':
      return personalFieldKeys.includes(fieldKey);
    case 'work':
      return workFieldKeys.includes(fieldKey);
    case 'auth':
      return authFieldKeys.includes(fieldKey);
    default:
      return false;
  }
};

/**
 * Get category for a specific field
 * @param fieldKey - The field key to get category for
 * @returns Category name or null if not found
 */
export const getFieldCategory = (fieldKey: string): 'personal' | 'work' | 'auth' | null => {
  if (personalFieldKeys.includes(fieldKey)) {
    return 'personal';
  }
  if (workFieldKeys.includes(fieldKey)) {
    return 'work';
  }
  if (authFieldKeys.includes(fieldKey)) {
    return 'auth';
  }
  return null;
};

/**
 * Sort fields within groups by predefined order
 * @param fields - Array of fields to sort
 * @param category - Category to determine sort order
 * @returns Sorted array of fields
 */
export const sortFieldsInCategory = (
  fields: FormField[],
  category: 'personal' | 'work' | 'auth'
): FormField[] => {
  const orderMap: Record<string, Record<string, number>> = {
    personal: {
      name: 0,
      surname: 1,
      identification_number: 2,
      phone_number: 3,
      email: 4,
    },
    work: {
      management: 0,
      office: 1,
      department: 2,
    },
    auth: {
      authorization_level: 0,
      role: 1,
    },
  };

  const categoryOrder = orderMap[category] || {};

  return fields.sort((a, b) => {
    const orderA = categoryOrder[a.key] ?? 999;
    const orderB = categoryOrder[b.key] ?? 999;
    return orderA - orderB;
  });
};
